/**
 * `@wr/i18n` — the typed string catalogue.
 *
 * Architecture §14 rule 10: every user-visible string resolves through here.
 * There are no string literals in JSX anywhere in `apps/web`, from Sprint 1 —
 * not because Sprint 1 needs a second language, but because retrofitting one
 * across a finished product is the expensive version of this decision.
 *
 * Deliberately tiny: a catalogue object, a lookup, `{placeholder}` substitution,
 * and types that make an unknown key a compile error. No pluralisation, no
 * date/number formatting, no lazy loading — those arrive with the second
 * locale, not before. Theo owns the catalogue *contents* from Sprint 2.
 */
import { en } from "./en.js";
export { en };
/** Locales with a complete catalogue. `en` is the fallback for every miss. */
export const LOCALES = ["en"];
export const DEFAULT_LOCALE = "en";
const CATALOGUES = { en };
const PLACEHOLDER = /\{(\w+)\}/g;
/**
 * Substitutes `{name}` placeholders. An unmatched placeholder is left in the
 * output verbatim: a visible `{count}` on screen is a bug report; a silently
 * empty string is not.
 */
export function interpolate(template, vars) {
    if (!vars)
        return template;
    return template.replace(PLACEHOLDER, (match, name) => {
        const value = vars[name];
        return value === undefined ? match : String(value);
    });
}
/** Resolve `key` in `locale`, falling back to `en`, then to the key itself. */
export function translate(locale, key, vars) {
    const template = CATALOGUES[locale]?.[key] ?? CATALOGUES[DEFAULT_LOCALE][key];
    // A missing key can only happen via an `as StringKey` cast; surfacing the raw
    // key beats rendering "undefined".
    if (template === undefined)
        return key;
    return interpolate(template, vars);
}
/** A translator bound to one locale. */
export function createTranslator(locale = DEFAULT_LOCALE) {
    return (key, vars) => translate(locale, key, vars);
}
/**
 * The default (`en`) translator, importable anywhere.
 *
 * S2 adds a React context so the locale is switchable at runtime; until a
 * second catalogue exists that would be ceremony. The call sites (`t("key")`)
 * do not change when it lands.
 */
export const t = createTranslator(DEFAULT_LOCALE);
export function isLocale(value) {
    return LOCALES.includes(value);
}
//# sourceMappingURL=index.js.map